<template>
    <section v-if="hasData" class="slice barometer-home__map">
        <div class="slice-content">
            <h2 class="slice-title">{{ t('barometer.freelancer.map.title', [generalBarometer.country]) }}</h2>
            <p>{{ t('barometer.freelancer.map.subtitle') }}</p>
            <div class="slice-body">
                <div id="map-geojson"></div>
                <ul class="barometer-home__legend">
                    <li v-for="({minimum, maximum, name}, i) in generalBarometer.areaSizeRange" :key="i" class="barometer-home__legend__item">
                        <div class="barometer-home__legend__chip" :class="getChipClass(name)" />
                        <div>
                            {{ t('barometer.freelancer.map.legend', [`${minimum}`, `${maximum}`]) }}
                        </div>
                    </li>
                </ul>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {inject, ref, onMounted} from 'vue';
    import {onUnmounted, useLocale, useTranslation} from '#imports';
    import {countryMapController} from '~/composables/barometer/countryMapController';
    import {BAROMETER_HOME} from '~/keys';
    import {GeneralBarometerFromJSON} from '~/openapi';

    const {generalBarometer} = inject(BAROMETER_HOME)!;
    const {t} = useTranslation();
    const {languageCode} = useLocale();

    const {loadCountryMap, destroyCountryMap, mapResize} = countryMapController({
        country: generalBarometer.countryCode!,
        area: generalBarometer.mapArea!,
        areas: generalBarometer.freelancersPerAreas!,
        lang: languageCode.value,
    });

    const hasData = ref(generalBarometer.areaSizeRange.length > 3);

    const allowedAreasNames = ['PERCENTILE_0_20', 'PERCENTILE_20_40', 'PERCENTILE_40_60', 'PERCENTILE_60_80', 'PERCENTILE_80_100'];

    function getChipClass(name: string) {
        if (allowedAreasNames.includes(name)) {
            const suffix = name.replace('PERCENTILE_', '');
            return `barometer-home__legend__chip--${suffix}`;
        }
        return '';
    }

    onMounted(async () => {
        await loadCountryMap('map-geojson');
        window.addEventListener('resize', mapResize);
    });

    onUnmounted(() => {
        window.removeEventListener('resize', mapResize);
        destroyCountryMap();
    });
</script>

<style lang="scss">
    .barometer-home {
        &__legend {
            display: grid;
            list-style: none;
            gap: var(--joy-core-spacing-4);
            grid-template-columns: repeat(3, minmax(0, 1fr));
            margin: var(--joy-core-spacing-8) auto 0;
            max-width: 700px;

            &__item {
                display: flex;
                gap: var(--joy-core-spacing-1);
                text-transform: lowercase;
            }
            &__chip {
                width: var(--joy-core-spacing-6);
                height: var(--joy-core-spacing-5);
                border-radius: var(--joy-core-radius-2);
                border: 1px solid var(--joy-color-neutral-30);
                background-color: var(--joy-color-neutral-50);

                &--0_20 {
                    background-color: #ffb9b7;
                }
                &--20_40 {
                    background-color: var(--joy-color-primary-30);
                }
                &--40_60 {
                    background-color: var(--joy-color-primary-50);
                }
                &--60_80 {
                    background-color: var(--joy-color-primary-70);
                }
                &--80_100 {
                    background-color: var(--joy-color-primary-90);
                }
            }
        }
    }
    #map-geojson {
        display: inline-block;
        aspect-ratio: 1 / 1;
        width: 100%;
        max-width: 700px;
        cursor: default !important;

        * {
            cursor: default !important;
        }
        div[role='dialog'] {
            user-select: none;
            // UX hack: ignore the tooltip hover for better xp with mouse moves over the map
            pointer-events: none;
        }
        div[role='dialog'] button {
            display: none !important;
        }
    }
</style>
